<template>
  <content-modal
    class="message-modal knowledge-base-delete-modal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    @modal-shown="actionShown"
    @modal-hidden="actionHidden"
    @modal-hide-request="actionHideRequest"
  >
    <template #title>
      {{
        params.title || t("lesson_activity_authoring_copilot_knowledge_base_delete_warning_title")
      }}
    </template>
    <template #content>
      <modal-content>
        <template #content>
          <div class="info">
            {{
              params.message ||
              t("lesson_activity_authoring_copilot_knowledge_base_delete_warning_message")
            }}
          </div>
        </template>
        <template #tools>
          <cv-button
            class="cancel"
            kind="tertiary"
            size="field"
            :disabled="isLoading"
            @click="actionHidden"
          >
            {{ t("button_cancel") }}
          </cv-button>
          <fetch-data-button
            :kind="deleteButton.kind"
            :size="deleteButton.size"
            :loading="isLoading"
            :disabled="isLoading || deleteButton.error"
            :error="deleteButton.error"
            @click="handleDeleteKnowledgeBase"
            @reset-error-state="handleDeleteButtonResetError"
          >
            <span v-if="deleteButton.error">{{ t("button_state_error") }}</span>
            <span v-else>{{ t("button_proceed") }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import modalEventBus from "@plugins/modal/service/event-bus";
import AuthoringCoPilotApi from "@apis/authoring.copilot.api";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm.ts";

// Props
const props = defineProps({
  params: { required: true, type: Object },
});
const { t } = useI18n();
const { vmNode } = useVm();

// Data
const closeAriaLabel = ref("Close");
const size = ref("small");
const visible = ref(true);
const autoHideOff = ref(true);
const isLoading = ref(false);
const deleteButton = ref({
  kind: "primary",
  size: "field",
  error: false,
  loading: false,
  errorMessage: t("api_action_generic_error_message"),
});

// Methods
const actionShown = () => {
  visible.value = true;
};

const actionHidden = () => {
  if (isLoading.value === false) {
    visible.value = false;
    modalEventBus.$emit("modal-hidden", vmNode.key);
  }
};

const actionHideRequest = (event) => {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    actionHidden();
  }
};

const resetState = () => {
  isLoading.value = false;
  visible.value = false;
};

const resetProcessingState = () => {
  deleteButton.value.loading = false;
  deleteButton.value.error = false;
};

const setProcessingError = () => {
  deleteButton.value.loading = false;
  deleteButton.value.error = true;
  // short delay before removing the error state.
  setTimeout(() => {
    resetProcessingState();
  }, 3000);
};

const handleDeleteButtonResetError = () => {
  deleteButton.value.error = false;
};

const handleDeleteKnowledgeBase = async () => {
  isLoading.value = true;

  if (props.params.ids.length === 0) {
    resetState();
    props.params.actionPrimary();
    return;
  }

  try {
    const response = await AuthoringCoPilotApi.deleteKnowledgeBase({
      ids: props.params.ids,
    });
    if (response.code === "ok") {
      resetState();
      props.params.actionPrimary();
    } else {
      setProcessingError();
    }
  } catch {
    setProcessingError();
  } finally {
    isLoading.value = false;
  }
};
</script>
